import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { DynamicTextFields } from '../../components/DynamicTextFields';
import { MeasurementField, Measurement } from '../../components/MeasurementField';
import { useCatalogMeasurements } from '../../hooks/useCatalogMeasurements';
import { useCatalog } from '../../hooks/useCatalog';
import { useUpload } from '../../hooks/useUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useRegisterSaveNow } from '../../hooks/useRegisterSaveNow';
import { logger } from '../../utils/logger';
import { en } from '../../translations/en';

/** Merge old per-section notes into a single 'notes' key (one-way migration). */
const mergeOldNotes = (stepNotes: Record<string, string[]>): Record<string, string[]> => {
  if (stepNotes.notes) return stepNotes;
  const oldKeys = Object.keys(stepNotes).filter(k => k !== 'notes');
  const merged = oldKeys.flatMap(k => stepNotes[k] || []).filter(Boolean);
  return merged.length > 0 ? { notes: merged } : {};
};

// Default dimensions that should always be visible
// Note: includes 'unit' field for consistency with migrated dimensions
const DEFAULT_DIMENSIONS: Measurement[] = [
  { type: 'Height', value: '', fraction: '', unit: 'inches' },
  { type: 'Width', value: '', fraction: '', unit: 'inches' }
];

interface MeasureTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
}

export const MeasureTab = ({ catalogItemId, ownerId }: MeasureTabProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentItem } = useCatalog();
  const { catalogId } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const { loadMeasurements, updateMeasurements } = useCatalogMeasurements(effectiveCatalogId, ownerId);
  const { markStepComplete } = useWizardCompletion();
  const { setStatus, setLastSaveTime } = useAutosaveStatus();

  // Load-failure guard — mirrors DescribeTab.hasError (GL#40).
  // When the initial GET fails, we hold all write paths blocked so that a
  // subsequent debounce or unmount-flush cannot write blank defaults over the
  // persisted row. The user must click Retry to re-attempt the load.
  const [hasError, setHasError] = useState(false);
  const hasErrorRef = useRef(false);
  useEffect(() => { hasErrorRef.current = hasError; }, [hasError]);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Initialize with the default state JSON so unmount doesn't trigger unwanted saves.
  // Note: When substrateMeasurements is undefined, it's omitted from JSON, matching the save() behavior.
  const lastSaveRef = useRef<string>(JSON.stringify({
    unit: 'inches',
    dimensions: DEFAULT_DIMENSIONS,
    hasDifferentSubstrate: false,
    stepNotes: {}
    // substrateMeasurements omitted when undefined (JSON.stringify removes undefined values)
  }));
  const loadedCatalogIdRef = useRef<string | null>(null);
  // isLoadingInitialData starts true so the autosave guard is engaged from
  // first render — protects against debounce/unmount-flush firing before the
  // load useEffect has run. The pre-existing `lastSaveRef === current`
  // equality check only protects while form state matches defaults; one
  // user keystroke breaks it. Sibling regression of DA-55L.2499 wipe.
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const isLoadingInitialDataRef = useRef(true);
  useEffect(() => { isLoadingInitialDataRef.current = isLoadingInitialData; }, [isLoadingInitialData]);
  const [dimensions, setDimensions] = useState<Measurement[]>(DEFAULT_DIMENSIONS);
  const [hasDifferentSubstrate, setHasDifferentSubstrate] = useState(false);
  const [substrateMeasurements, setSubstrateMeasurements] = useState<{ unit: string; dimensions: Measurement[] }>({ unit: 'inches', dimensions: [] });
  const [stepNotes, setStepNotes] = useState<Record<string, string[]>>({});

  // Initialize substrate dimensions with defaults when checkbox is enabled
  useEffect(() => {
    if (hasDifferentSubstrate && substrateMeasurements.dimensions.length === 0) {
      setSubstrateMeasurements(prev => ({
        ...prev,
        dimensions: DEFAULT_DIMENSIONS
      }));
    }
  }, [hasDifferentSubstrate, substrateMeasurements.dimensions.length]);

  // Load existing measurements on mount or when catalogId changes.
  // On error: set hasError=true and do NOT release isLoadingInitialData so
  // the autosave guard stays engaged — prevents blank-default writes (GL#40).
  useEffect(() => {
    // Only load if we haven't already loaded this specific catalogId
    if (effectiveCatalogId && loadedCatalogIdRef.current !== effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      loadMeasurements().then((data) => {
        if (data) {
          const globalUnit = data.unit || 'inches';
          // Migrate: apply global unit to dims that lack a per-dimension unit
          const migrateDims = (dims: Measurement[]) =>
            dims.map((d) => ({ ...d, unit: d.unit || globalUnit }));
          // Preserve default dimensions (Height/Width) if no dimensions exist
          setDimensions(
            data.dimensions?.length > 0 ? migrateDims(data.dimensions) : DEFAULT_DIMENSIONS
          );
          setHasDifferentSubstrate(data.hasDifferentSubstrate || false);
          const substrate = data.substrateMeasurements || { unit: 'inches', dimensions: [] };
          setSubstrateMeasurements({
            ...substrate,
            dimensions: migrateDims(substrate.dimensions),
          });
          setStepNotes(mergeOldNotes(data.stepNotes || {}));

          // Update completion tracking after data loads
          markStepComplete('measure', 0, (data.dimensions || []).length > 0);
          setIsLoadingInitialData(false);
        } else {
          setIsLoadingInitialData(false);
        }
      }).catch((err) => {
        // Load failed — engage the hasError guard. Once hasError is true, EVERY
        // write path (saveMeasurements early-return, debouncedSave→save, and the
        // unmount-flush via hasErrorRef) is blocked, so a later autosave cannot
        // write blank defaults over the persisted row. isLoadingInitialData is
        // released (the error view renders instead of the form); hasError is the guard.
        logger.error('MeasureTab: Failed to load measurements', err);
        setHasError(true);
        setIsLoadingInitialData(false);
      });
    } else if (!effectiveCatalogId) {
      // Fresh-create flow: no id to load. Clear the guard so saves can
      // proceed once an id arrives.
      setIsLoadingInitialData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId]);

  // Smart auto-save function
  const saveMeasurements = useCallback(async () => {
    // Guard: never save during initial load OR after a load failure (GL#40).
    if (!effectiveCatalogId || isLoadingInitialData || hasError) {
      return;
    }

    const measurementData = {
      unit: 'inches', // Legacy fallback for older consumers; per-dimension units in dim.unit
      dimensions,
      hasDifferentSubstrate,
      substrateMeasurements: hasDifferentSubstrate ? substrateMeasurements : undefined,
      stepNotes
    };

    const currentDataString = JSON.stringify(measurementData);
    if (currentDataString === lastSaveRef.current) {
      return;
    }

    setStatus('saving');
    try {
      await updateMeasurements(measurementData);
      lastSaveRef.current = currentDataString;

      // Update completion tracking
      markStepComplete('measure', 0, dimensions.length > 0);

      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (error) {
      setStatus('error');
      logger.error('MeasureTab - Failed to save measurements', error);
    }
  }, [effectiveCatalogId, isLoadingInitialData, hasError, updateMeasurements, dimensions, hasDifferentSubstrate, substrateMeasurements, stepNotes, markStepComplete, setStatus, setLastSaveTime]);

  // Ref always points to the latest save function — avoids stale closure in setTimeout
  const saveMeasurementsRef = useRef(saveMeasurements);
  useEffect(() => { saveMeasurementsRef.current = saveMeasurements; }, [saveMeasurements]);
  // Let the wizard "Finished" button flush this tab's pending save.
  useRegisterSaveNow(saveMeasurementsRef, saveTimeoutRef);

  const debouncedSave = useCallback(() => {
    // Signal immediately that there are unsaved edits — clears "All changes saved" (GL#40 Part 1).
    setStatus('dirty');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveMeasurementsRef.current(), 2000);
  }, [setStatus]); // setStatus is stable from context

  // Cleanup timeout and flush any pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      // Never flush during initial load or after load failure — would wipe row (GL#40).
      if (isLoadingInitialDataRef.current || hasErrorRef.current) return;
      try {
        saveMeasurementsRef.current()?.catch((err: unknown) =>
          logger.error('Flush save failed on unmount', err)
        );
      } catch (err) {
        logger.error('Flush save failed on unmount (sync)', err);
      }
    };
  }, []);

  // Retry handler: clear the error flag and reset the load sentinel so the
  // load effect re-runs (mirrors DescribeTab's retry pattern — GL#40).
  const handleRetry = () => {
    setHasError(false);
    loadedCatalogIdRef.current = null;
    setIsLoadingInitialData(true);
    loadMeasurements().then((data) => {
      if (data) {
        const globalUnit = data.unit || 'inches';
        const migrateDims = (dims: Measurement[]) =>
          dims.map((d) => ({ ...d, unit: d.unit || globalUnit }));
        setDimensions(
          data.dimensions?.length > 0 ? migrateDims(data.dimensions) : DEFAULT_DIMENSIONS
        );
        setHasDifferentSubstrate(data.hasDifferentSubstrate || false);
        const substrate = data.substrateMeasurements || { unit: 'inches', dimensions: [] };
        setSubstrateMeasurements({
          ...substrate,
          dimensions: migrateDims(substrate.dimensions),
        });
        setStepNotes(mergeOldNotes(data.stepNotes || {}));
        markStepComplete('measure', 0, (data.dimensions || []).length > 0);
        setIsLoadingInitialData(false);
        if (effectiveCatalogId) loadedCatalogIdRef.current = effectiveCatalogId;
      } else {
        setIsLoadingInitialData(false);
        if (effectiveCatalogId) loadedCatalogIdRef.current = effectiveCatalogId;
      }
    }).catch((err) => {
      logger.error('MeasureTab: Failed to load measurements on retry', err);
      setHasError(true);
      setIsLoadingInitialData(false);
    });
  };

  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Something went wrong loading the measurements form.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please try again or contact support if the issue persists.
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />
      <WizardAccordion
        id="measure-dimensions"
        title={en.wizard.measure.measurements.title}
        subtitle={en.wizard.measure.measurements.subtitle}
        defaultExpanded={true}
        summary={(() => {
          const filled = dimensions.filter(d => (d.value || '').toString().trim());
          if (filled.length === 0) return 'No dimensions set';
          const parts = filled.slice(0, 2).map(d => {
            const v = d.fraction ? `${d.value} ${d.fraction}` : d.value;
            return `${v}${d.unit ? ' ' + d.unit : ''}`;
          });
          return parts.join(' × ');
        })()}
        privacyFieldPath="measure.dimensions"
      >
        <MeasurementField
          dimensions={dimensions}
          onDimensionsChange={(newDimensions) => {
            setDimensions(newDimensions);
            debouncedSave();
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={hasDifferentSubstrate}
              onChange={(e) => {
                setHasDifferentSubstrate(e.target.checked);
                debouncedSave();
              }}
            />
          }
          label={en.wizard.measure.measurements.substrateCheckbox}
          sx={{ mt: 2 }}
        />
      </WizardAccordion>

      {hasDifferentSubstrate && (
        <WizardAccordion
          title={en.wizard.measure.substrateMeasurements.title}
          subtitle={en.wizard.measure.substrateMeasurements.subtitle}
          defaultExpanded={!isMobile}
          privacyFieldPath="measure.substrateDimensions"
        >
          <MeasurementField
            dimensions={substrateMeasurements.dimensions}
            onDimensionsChange={(newDimensions) => {
              setSubstrateMeasurements(prev => ({ ...prev, dimensions: newDimensions }));
              debouncedSave();
            }}
          />
        </WizardAccordion>
      )}

      <WizardAccordion
        title={en.wizard.additionalNotes.title}
        subtitle={en.wizard.additionalNotes.subtitle}
        privacyFieldPath="measure.notes"
      >
        <DynamicTextFields
          label={en.wizard.describe.additionalNotes.label}
          addButtonText={en.wizard.describe.additionalNotes.addButton}
          placeholder={en.wizard.describe.additionalNotes.placeholder}
          initialValues={stepNotes.notes || []}
          onChange={(notes) => {
            setStepNotes(prev => ({ ...prev, notes }));
            debouncedSave();
          }}
        />
      </WizardAccordion>
    </Box>
  );
};
